import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import moment from "moment";
import "moment/locale/es";
import supabase from "../../../supabase";
import { LoaderSpinner } from "../../../components/spinner/loader";
import DataTable from "../../../components/ui/DataTable";
import DateRangeFilter from "../../../components/ui/DateRangeFilter";
import { Num, SectionCard } from "../../../components/ui";
import { RankBars, TimeBars } from "../../../components/ui/charts";
import { tokens } from "../../../theme";
import { ticketObjectStatus } from "../../../constants/ticketStatus";
import { formatCompactCurrency, formatCurrencyManual } from "../../../utils/formatters";

moment.locale("es");

/** Neto e IVA de una factura, a partir de sus renglones. */
const ticketAmounts = (ticket) =>
  (ticket.ticketDetails || []).reduce(
    (acc, detail) => {
      const neto = (parseFloat(detail.price) || 0) * (parseFloat(detail.quantity) || 0);
      const rate = parseFloat(detail.ivaPercentage) || 0;
      const iva = neto * (rate / 100);
      return {
        neto: acc.neto + neto,
        iva: acc.iva + iva,
        iva105: acc.iva105 + (rate === 10.5 ? iva : 0),
        iva21: acc.iva21 + (rate === 21 ? iva : 0),
      };
    },
    { neto: 0, iva: 0, iva105: 0, iva21: 0 }
  );

/**
 * Reporte de facturación.
 *
 * Sale de la base del sistema: facturas emitidas (autorizadas o ya pagadas),
 * excluyendo las canceladas y las que todavía están pendientes de emitir.
 */
const BillingReport = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [range, setRange] = useState({
    from: moment().startOf("year").toDate(),
    to: moment().endOf("day").toDate(),
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from("tickets")
        .select(
          "ticketId, ticketNumber, ticketDate, status, client: clientId(clientId, company, cuit), ticketDetails(price, quantity, ivaPercentage)"
        )
        .in("status", [ticketObjectStatus.autorizada, ticketObjectStatus.pagada])
        .limit(5000);

      if (range.from) query = query.gte("ticketDate", moment(range.from).startOf("day").toISOString());
      if (range.to) query = query.lte("ticketDate", moment(range.to).endOf("day").toISOString());

      const { data, error: err } = await query;
      if (err) throw new Error(err.message);
      setTickets(data || []);
    } catch (e) {
      setError(e.message || "No se pudo armar el reporte");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const { byClient, byMonth, totals } = useMemo(() => {
    const clients = new Map();
    const months = new Map();
    const acc = { total: 0, neto: 0, iva: 0, iva105: 0, iva21: 0, facturas: 0 };

    tickets.forEach((ticket) => {
      const amounts = ticketAmounts(ticket);
      const total = amounts.neto + amounts.iva;
      if (!total) return;

      acc.total += total;
      acc.neto += amounts.neto;
      acc.iva += amounts.iva;
      acc.iva105 += amounts.iva105;
      acc.iva21 += amounts.iva21;
      acc.facturas += 1;

      const clientId = ticket.client?.clientId || "sin";
      const currentClient =
        clients.get(clientId) || {
          clientId,
          company: ticket.client?.company || "Sin cliente",
          cuit: ticket.client?.cuit || "",
          facturas: 0,
          neto: 0,
          iva105: 0,
          iva21: 0,
          total: 0,
        };
      currentClient.facturas += 1;
      currentClient.neto += amounts.neto;
      currentClient.iva105 += amounts.iva105;
      currentClient.iva21 += amounts.iva21;
      currentClient.total += total;
      clients.set(clientId, currentClient);

      const monthKey = moment(ticket.ticketDate).format("YYYY-MM");
      months.set(monthKey, (months.get(monthKey) || 0) + total);
    });

    // Se completan los meses vacíos: un hueco también es información.
    const series = [];
    if (range.from && range.to) {
      const cursor = moment(range.from).startOf("month");
      const end = moment(range.to).endOf("month");
      while (cursor.isSameOrBefore(end) && series.length < 36) {
        const key = cursor.format("YYYY-MM");
        const value = months.get(key) || 0;
        series.push({
          key,
          label: cursor.format("MMMM [de] YYYY"),
          short: cursor.format("MMM"),
          value,
          display: formatCompactCurrency(value),
        });
        cursor.add(1, "month");
      }
    }

    return {
      byClient: [...clients.values()].sort((a, b) => b.total - a.total),
      byMonth: series,
      totals: acc,
    };
  }, [tickets, range]);

  const term = search.trim().toLowerCase();
  const visibleClients = term
    ? byClient.filter((row) =>
        [row.company, row.cuit].filter(Boolean).some((v) => String(v).toLowerCase().includes(term))
      )
    : byClient;

  const topClients = byClient.slice(0, 10).map((row) => ({
    key: row.clientId,
    label: row.company,
    value: row.total,
    display: formatCurrencyManual(row.total),
    meta: totals.total > 0 ? `${Math.round((row.total / totals.total) * 100)}%` : undefined,
  }));

  const amountColumn = (name, label, bold = false) => ({
    name,
    label,
    options: {
      filter: false,
      sort: true,
      setCellProps: () => ({ style: { width: 140, textAlign: "right", whiteSpace: "nowrap" } }),
      setCellHeaderProps: () => ({ style: { width: 140, textAlign: "right" } }),
      customBodyRender: (value) => (
        <Num sx={{ fontSize: bold ? "13.5px" : "13px", fontWeight: bold ? 600 : 400, color: bold ? tokens.ink : tokens.ink2 }}>
          {formatCurrencyManual(value)}
        </Num>
      ),
    },
  });

  const columns = [
    {
      name: "company",
      label: "Cliente",
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = visibleClients[dataIndex];
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
              <Typography sx={{ fontSize: "13.5px", fontWeight: 500 }}>{row.company}</Typography>
              <Typography sx={{ fontSize: "11.5px", color: tokens.ink3, fontVariantNumeric: "tabular-nums" }}>
                {row.cuit || "Sin CUIT"} · {row.facturas}{" "}
                {row.facturas === 1 ? "factura" : "facturas"}
              </Typography>
            </Box>
          );
        },
      },
    },
    amountColumn("neto", "Neto"),
    amountColumn("iva105", "IVA 10,5%"),
    amountColumn("iva21", "IVA 21%"),
    amountColumn("total", "Total", true),
  ];

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240 }}>
        <LoaderSpinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, paddingY: 6 }}>
        <Typography sx={{ color: tokens.ink2 }}>{error}</Typography>
        <Button variant="outlined" onClick={load}>
          Reintentar
        </Button>
      </Box>
    );
  }

  const kpis = [
    { label: "Facturado", value: formatCurrencyManual(totals.total) },
    { label: "Facturas emitidas", value: String(totals.facturas) },
    {
      label: "Factura promedio",
      value: formatCurrencyManual(totals.facturas ? totals.total / totals.facturas : 0),
    },
    { label: "IVA del período", value: formatCurrencyManual(totals.iva) },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <DateRangeFilter
          from={range.from}
          to={range.to}
          label="Período"
          onChange={(from, to) => setRange({ from, to })}
        />
        <Typography sx={{ fontSize: "12.5px", color: tokens.ink3 }}>
          Facturas emitidas (autorizadas y pagadas). No incluye canceladas ni pendientes.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "10px",
        }}
      >
        {kpis.map((kpi) => (
          <Box
            key={kpi.label}
            sx={{
              backgroundColor: tokens.surface,
              border: `1px solid ${tokens.border}`,
              borderRadius: `${tokens.radiusCard}px`,
              padding: "13px 15px",
              display: "flex",
              flexDirection: "column",
              gap: "5px",
              minWidth: 0,
            }}
          >
            <Typography sx={{ fontSize: "11.5px", fontWeight: 500, color: tokens.ink2 }}>
              {kpi.label}
            </Typography>
            <Num sx={{ fontSize: "19px", fontWeight: 600, lineHeight: 1.1 }}>{kpi.value}</Num>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 400px" },
          gap: "12px",
          alignItems: "start",
        }}
      >
        <SectionCard title="Facturación por mes" subtitle="Total emitido en cada mes del período">
          <Box sx={{ padding: "16px 18px 18px" }}>
            <TimeBars data={byMonth} />
          </Box>
        </SectionCard>

        <SectionCard
          title="Top 10 clientes"
          subtitle={
            totals.total > 0
              ? `Concentran el ${Math.round(
                  (topClients.reduce((a, c) => a + c.value, 0) / totals.total) * 100
                )}% de lo facturado`
              : undefined
          }
        >
          <Box sx={{ padding: "16px 18px 18px" }}>
            <RankBars data={topClients} />
          </Box>
        </SectionCard>
      </Box>

      <DataTable
        data={visibleClients}
        columns={columns}
        heading="Facturación por cliente"
        search={{
          value: search,
          onChange: setSearch,
          placeholder: "Buscar por cliente o CUIT…",
        }}
        resultCount={visibleClients.length}
        totalCount={byClient.length}
        emptyMessage="No hay facturas emitidas en el período elegido"
        options={{
          sortOrder: { name: "total", direction: "desc" },
          rowsPerPage: 25,
          downloadOptions: { filename: "facturacion-por-cliente.csv", separator: ";" },
        }}
      />
    </Box>
  );
};

export default BillingReport;
